import SwiperCarousel from '../utils/swiper-carousel';
import './mobile-menu.css';

/**
 * Rolodex Animation Controller
 * Manages the rotating placeholder text animation with 3D flip effect
 */
class RolodexAnimation {
  constructor(container, rotationInterval = 3000) {
    this.container = container;
    this.allItems = Array.from(container.querySelectorAll('.search-placeholder-text'));
    this.mobileOnly = this.allItems.find(item => item.dataset.mobileText === 'Search');
    this.items = this.allItems.filter(item => item.dataset.mobileText !== 'Search');
    this.currentIndex = 0;
    this.rotationInterval = rotationInterval; // Configurable rotation interval
    this.isAnimating = false;
    this.isPaused = false;
    this.intervalId = null;
    this.isMobile = window.innerWidth <= 400;
    
    // Set initial state based on screen size
    if (this.isMobile) {
      this.showMobileOnly();
    } else if (this.items.length > 0) {
      this.hideMobileOnly();
      this.init();
    }
    
    // Handle responsive behavior
    this.handleResize();
  }
  
  init() {
    // Calculate and set container width based on longest text
    this.setContainerWidth();
    
    // Start the rotation
    this.startRotation();
    
    // Pause on hover for better UX
    this.container.addEventListener('mouseenter', () => this.pause());
    this.container.addEventListener('mouseleave', () => this.resume());
    
    // Handle focus events for accessibility
    const searchButton = this.container.closest('[data-search-trigger]');
    if (searchButton) {
      searchButton.addEventListener('focus', () => this.pause());
      searchButton.addEventListener('blur', () => this.resume());
    }
    
    // Handle reduced motion preference
    this.handleReducedMotion();
  }
  
  startRotation() {
    this.intervalId = setInterval(() => {
      if (!this.isPaused && !this.isAnimating) {
        this.rotate();
      }
    }, this.rotationInterval);
  }
  
  rotate() {
    if (this.isAnimating || this.items.length <= 1) return;
    
    this.isAnimating = true;
    const currentItem = this.items[this.currentIndex];
    const nextIndex = (this.currentIndex + 1) % this.items.length;
    const nextItem = this.items[nextIndex];
    
    // Add exit animation to current item
    currentItem.classList.add('exit');
    currentItem.classList.remove('active');
    
    // Prepare next item
    nextItem.classList.add('enter');
    
    // Force reflow to ensure enter class is applied
    // eslint-disable-next-line no-unused-expressions
    nextItem.offsetHeight;
    
    // Start enter animation
    requestAnimationFrame(() => {
      nextItem.classList.remove('enter');
      nextItem.classList.add('active');
      
      // Clean up after animation completes
      setTimeout(() => {
        currentItem.classList.remove('exit');
        this.currentIndex = nextIndex;
        this.isAnimating = false;
      }, 800); // Match CSS transition duration for flip clock effect
    });
  }
  
  pause() {
    this.isPaused = true;
  }
  
  resume() {
    this.isPaused = false;
  }
  
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
  
  handleReducedMotion() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e) => {
      if (e.matches) {
        // Reduced motion preferred - use simple fade
        this.rotationInterval = 4000; // Slower rotation
      } else {
        this.rotationInterval = 3000;
      }
      
      // Restart rotation with new interval
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.startRotation();
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    handleChange(mediaQuery);
  }
  
  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 400;
        
        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            // Switching to mobile - show only "Search"
            this.destroy();
            this.showMobileOnly();
          } else {
            // Switching from mobile - start rotation
            this.hideMobileOnly();
            if (this.items.length > 0) {
              this.init();
            }
          }
        }
      }, 250);
    });
  }
  
  showMobileOnly() {
    if (this.mobileOnly) {
      this.mobileOnly.classList.add('active');
      this.items.forEach(item => {
        item.classList.remove('active', 'exit', 'enter');
      });
      
      // Remove any inline width styles for mobile
      const parentContainer = this.container.parentElement;
      if (parentContainer && parentContainer.classList.contains('search-placeholder-container')) {
        parentContainer.style.width = '';
      }
      
      // Fade in the search button on mobile
      const searchButton = document.getElementById('header-search-button');
      if (searchButton) {
        requestAnimationFrame(() => {
          searchButton.classList.remove('opacity-0');
          searchButton.classList.add('opacity-100');
        });
      }
    }
  }
  
  hideMobileOnly() {
    if (this.mobileOnly) {
      this.mobileOnly.classList.remove('active');
      if (this.items.length > 0) {
        this.items[0].classList.add('active');
      }
      
      // Reset width for desktop (but will be recalculated in init)
      const parentContainer = this.container.parentElement;
      if (parentContainer && parentContainer.classList.contains('search-placeholder-container')) {
        parentContainer.style.width = '';
      }
    }
  }
  
  setContainerWidth() {
    // Skip width calculation on mobile
    if (this.isMobile) return;
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Create a temporary element to measure text widths
      const measurer = document.createElement('span');
      measurer.style.position = 'absolute';
      measurer.style.visibility = 'hidden';
      measurer.style.whiteSpace = 'nowrap';
      
      // Copy exact styles from the actual text elements
      const firstItem = this.items[0] || this.mobileOnly;
      if (!firstItem) return;
      
      const textStyles = window.getComputedStyle(firstItem);
      measurer.style.fontSize = textStyles.fontSize;
      measurer.style.fontFamily = textStyles.fontFamily;
      measurer.style.fontWeight = textStyles.fontWeight;
      measurer.style.letterSpacing = textStyles.letterSpacing;
      
      document.body.appendChild(measurer);
      
      let maxWidth = 0;
      
      // Find the widest text
      this.items.forEach(item => {
        measurer.textContent = item.textContent.trim();
        const width = measurer.offsetWidth;
        if (width > maxWidth) {
          maxWidth = width;
        }
      });
      
      // Clean up
      document.body.removeChild(measurer);
      
      // Calculate final width
      // Add width for "Search for " prefix (measure it properly)
      const tempSpan = document.createElement('span');
      tempSpan.style.cssText = measurer.style.cssText;
      tempSpan.textContent = 'Search for ';
      document.body.appendChild(tempSpan);
      const searchForWidth = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      
      const finalWidth = maxWidth + searchForWidth + 10; // Add the prefix width plus buffer
      
      // Set the container width (only for desktop - above 400px)
      if (!this.isMobile) {
        // Set the parent container width
        const parentContainer = this.container.parentElement;
        
        if (parentContainer && parentContainer.classList.contains('search-placeholder-container')) {
          parentContainer.style.width = `${finalWidth}px`;
        }
        
        // Fade in the search button after setting width
        const searchButton = document.getElementById('header-search-button');
        if (searchButton) {
          // Trigger opacity change on next frame to ensure transition works
          requestAnimationFrame(() => {
            searchButton.classList.remove('opacity-0');
            searchButton.classList.add('opacity-100');
          });
        }
      }
    });
  }
}

export default (element, data) => {
  // CONSTANTS:
  const SCROLL_THRESHOLD = 220; // Scroll threshold for header transparency
  
  // ELEMENTS:
  const body = document.querySelector('body');
  const promoBar = document.querySelector('.promo-bar');
  const submenus = element.querySelectorAll('[data-submenu]');
  const header = element.parentElement;
  const secondaryNav = element.querySelector('.menu-secondary-nav');
  const mobileNavMain = element.querySelector('.mobile-nav-main');

  const mobileMenuTrigger = element.querySelector('[data-nav-trigger]');
  const mobileMenuInner = element.querySelector('[data-mobile-menu-inner]');
  const expandableMenus = element.querySelectorAll('[data-expandable-nav]');
  const closeMenuTrigger = element.querySelector('[data-close-menu]');
  const closeMobileSubMenuTriggers = element.querySelectorAll('[data-close-submenu]');
  const mobileMenuOverlay = element.querySelector('[data-mobile-menu-overlay]');
  const desktopNavItems = element.querySelectorAll('[data-nav-link]');
  const desktopParentNavItems = element.querySelectorAll('[data-nav-item]');
  const mainContentOverlay = element.querySelector('[data-main-content-overlay]');

  // SEARCH RELATED ELEMENTS
  const searchTrigger = element.querySelectorAll('[data-search-trigger]');
  const searchDrawer = element.querySelector('[data-search-drawer]');
  const searchDrawerClose = element.querySelector('[data-search-drawer-close]');
  const searchDrawerSlider = element.querySelector('[data-search-drawer-slider]');
  const searchDrawerScrollbal = element.querySelector('[data-search-drawer-scrollbar]');
  const searchDrawerSliderPrev = element.querySelector('[data-search-drawer-slider-prev]');
  const searchDrawerSliderNext = element.querySelector('[data-search-drawer-slider-next]');

  // Near top with other element declarations
  const overlayMenu = element.classList.contains('overlay-menu') ? element : null;
  let headerStateTimeout;
  let rafId;
  let lastScrollY = 0;
  let isSecondaryNavHidden = false;

  // Throttle helper
  const throttle = (func, limit) => {
    let inThrottle;
    let lastFunc;
    let lastRan;

    return function throttled(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        lastRan = Date.now();
        inThrottle = true;
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  };


  // Handle header transparency - only if overlay menu exists AND transparent hero is active
  const handleHeaderScroll = throttle(() => {
    if (!overlayMenu) return;
    
    // Only handle transparency if a hero section has requested it
    const hasActiveTransparentHero = element.classList.contains('hero-transparent-active');
    if (!hasActiveTransparentHero) return;

    const isScrolled = window.scrollY > SCROLL_THRESHOLD;

    // Force a class check/update on each scroll
    if (isScrolled) {
      overlayMenu.classList.remove('bg-transparent');
      overlayMenu.classList.add('bg-white');
    } else {
      overlayMenu.classList.remove('bg-white');
      overlayMenu.classList.add('bg-transparent');
    }

    // Cancel any existing timeout/RAF
    if (headerStateTimeout) clearTimeout(headerStateTimeout);
    if (rafId) cancelAnimationFrame(rafId);

    // Start a brief monitoring period after scroll
    let checkCount = 0;
    const checkHeaderState = () => {
      checkCount += 1;
      if (checkCount > 10) return; // Stop after ~10 frames (~160ms)

      if (window.scrollY <= SCROLL_THRESHOLD && overlayMenu.classList.contains('bg-white')) {
        overlayMenu.classList.remove('bg-white');
        overlayMenu.classList.add('bg-transparent');
      }
      rafId = requestAnimationFrame(checkHeaderState);
    };

    headerStateTimeout = setTimeout(() => {
      rafId = requestAnimationFrame(checkHeaderState);
    }, 100); // Start monitoring 100ms after last scroll event
  }, 50);

  // EVENTS:
  const headerEventsHandler = () => {
    let trapFocusCleanup = null; // Moved here for broader scope
    let mobileMenuTrapCleanup = null; // Separate trap for main mobile menu
    
    // Initialize all close submenu buttons as non-clickable
    const initializeCloseButtons = () => {
      const allCloseSubmenuBtns = element.querySelectorAll('[data-close-submenu]');
      allCloseSubmenuBtns.forEach((btn) => {
        btn.setAttribute('tabindex', '-1');
        // Use setAttribute to avoid no-param-reassign error
        btn.setAttribute('style', 'pointer-events: none;');
      });
    };
    initializeCloseButtons();
    
    const closeMobileMenu = () => {
      // Clean up focus trap if it exists
      if (trapFocusCleanup) {
        trapFocusCleanup();
        trapFocusCleanup = null;
      }
      
      // Clean up main mobile menu trap
      if (mobileMenuTrapCleanup) {
        mobileMenuTrapCleanup();
        mobileMenuTrapCleanup = null;
      }
      
      // Reset all close submenu button tabindex values and pointer events
      const allCloseSubmenuBtns = element.querySelectorAll('[data-close-submenu]');
      allCloseSubmenuBtns.forEach((btn) => {
        btn.setAttribute('tabindex', '-1');
        // Use setAttribute to avoid no-param-reassign error
        btn.setAttribute('style', 'pointer-events: none;');
      });
      
      // Close any expanded mobile navigation sections before closing the menu
      const expandedSections = element.querySelectorAll('[data-mobile-menu-link].expanded');
      expandedSections.forEach((section) => {
        const expandableButton = section.querySelector('[data-expandable-nav]');
        if (expandableButton) {
          expandableButton.click();
        }
      });
      
      body.classList.remove('open-menu');
      body.classList.remove('has-expanded-mobile-nav-section');
    };

    const toggleMobileMenu = () => {
      body.classList.toggle('open-menu');

      // this logic fixes a safari iOS bug - see https://meetdomaine.atlassian.net/browse/SAJ-1454
      setTimeout(() => {
        if (body.classList.contains('open-menu')) {
          body.querySelectorAll('[data-mobile-menu-link]')
            .forEach((link) => {
              link.classList.add('w-full');
            });
          
          // Set up focus trap for mobile menu when opened
          const mobileMenu = element.querySelector('[data-mobile-menu]');
          if (mobileMenu && !mobileMenuTrapCleanup) {
            mobileMenuTrapCleanup = trapFocus(mobileMenu, closeMobileMenu);
          }
        } else {
          body.querySelectorAll('[data-mobile-menu-link]')
            .forEach((link) => {
              link.classList.remove('w-full');
            });
          
          // Clean up mobile menu trap when closing
          if (mobileMenuTrapCleanup) {
            mobileMenuTrapCleanup();
            mobileMenuTrapCleanup = null;
          }
        }
      }, 75);

      updateHeaderPosition();
      updateMobileMenuPosition();
      updateClosingEvent();
    };

    const updateClosingEvent = () => {
      if (body.classList.contains('open-menu')) {
        document.addEventListener('keyup', (e) => {
          if (e.key === 'Escape') {
            closeMobileMenu();
          }
        });
      } else {
        document.removeEventListener('keyup', closeMobileMenu);
      }
    };

    const updateHeaderPosition = () => {
      const isDesktop = data.layout.bps.above.xl.matches && data.layout.bps.above.desktop.matches;
      const promoBarHeight = promoBar ? promoBar.offsetHeight : 0;

      header.style.top = isDesktop ? `${promoBarHeight}px` : '0px';
      if (isDesktop) {
        const headerSection = document.getElementById('shopify-section-header');
        const headerHeight = headerSection ? headerSection.offsetHeight : 0;
        
        submenus.forEach((menu) => {
          const menuStyle = menu.style;
          menuStyle.top = `${headerHeight}px`;
        });

        // Update mainContentOverlay top position
        if (mainContentOverlay) {
          mainContentOverlay.style.top = `${headerHeight}px`;
        }

        closeMobileMenu();
      }

      if (!isDesktop) {
        const mobilePromoBarHeight = window.scrollY <= 0 ? promoBarHeight : 0;
        const menuInnerHeight = window.innerHeight - mobilePromoBarHeight - header.offsetHeight;
        mobileMenuInner.style.height = `${menuInnerHeight}px`;
      }
    };

    // Update mobile menu position dynamically
    const updateMobileMenuPosition = () => {
      const mobileMenu = element.querySelector('[data-mobile-menu]');
      if (mobileMenu) {
        const headerSection = document.getElementById('shopify-section-header');
        const promoBarSection = document.querySelector('[data-section="promo-bar"]');
        
        const headerHeight = headerSection ? headerSection.offsetHeight : 0;
        const currentPromoBarHeight = promoBarSection ? promoBarSection.offsetHeight : 0;
        
        // Below 768px: position at top 0
        // 768px and above: adjust based on scroll position
        if (window.innerWidth < 768) {
          mobileMenu.style.top = '0';
        } else {
          // Check if promo bar is scrolled out of view
          const { scrollY } = window;
          let topOffset;
          
          if (scrollY >= currentPromoBarHeight) {
            // Promo bar is out of view, only use header height
            topOffset = headerHeight;
          } else {
            // Promo bar is still visible, use full height
            topOffset = headerHeight + currentPromoBarHeight;
          }
          
          mobileMenu.style.top = `${topOffset}px`;
        }
      }
    };

    // Handle secondary nav hide/show on scroll
    const handleSecondaryNavScroll = () => {
      if (!secondaryNav) return;
      
      // Only hide/show on mobile
      if (data.layout.bps.above.desktop.matches) return; // treat >=1250px as desktop
      
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY;
      const mobileNavHeader = element.querySelector('.mobile-nav-header');
      
      // Skip small scroll deltas to prevent jitter
      if (Math.abs(scrollDelta) < 3) return;
      
      // Scrolling down - hide secondary nav
      if (scrollDelta > 0 && currentScrollY > 20 && !isSecondaryNavHidden) {
        mobileNavHeader.classList.add('secondary-nav-scrolled');
        isSecondaryNavHidden = true;
      }
      // Scrolling up - show secondary nav
      else if (scrollDelta < -5 && isSecondaryNavHidden) {
        mobileNavHeader.classList.remove('secondary-nav-scrolled');
        isSecondaryNavHidden = false;
      }
      
      lastScrollY = currentScrollY;
      
      // Update mobile menu position when scrolling
      updateHeaderPosition();
    };

    // Handle general scroll events to update mobile menu position
    const handleGeneralScroll = throttle(() => {
      // Update mobile menu position when promo bar scrolls out of view
      updateMobileMenuPosition();
    }, 50);

    // Focus trap helper
    const trapFocus = (container, onEscape = null) => {
      const focusableElements = container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length === 0) return null;
      
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      
      // Focus the first element
      firstFocusable.focus();
      
      const handleKeyDown = (e) => {
        // Handle Tab for focus trap
        if (e.key === 'Tab') {
          // If shift + tab on first element, go to last
          if (e.shiftKey && document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
          // If tab on last element, go to first
          else if (!e.shiftKey && document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
        // Handle Escape callback if provided
        else if (e.key === 'Escape' && onEscape) {
          e.preventDefault();
          onEscape();
        }
      };
      
      container.addEventListener('keydown', handleKeyDown);
      
      // Return cleanup function
      return () => {
        container.removeEventListener('keydown', handleKeyDown);
      };
    };
    
    expandableMenus.forEach((menu) => {
      menu.addEventListener('click', () => {
        // Clean up previous focus trap if it exists
        if (trapFocusCleanup) {
          trapFocusCleanup();
          trapFocusCleanup = null;
        }
        
        expandableMenus.forEach((el) => {
          const parentEl = el.parentElement;
          const closeSubmenuBtn = parentEl.querySelector('[data-close-submenu]');
          
          if (el === menu) {
            parentEl.classList.toggle('expanded');
            // Update tabindex based on expanded state with slight delay for DOM updates
            if (closeSubmenuBtn) {
              if (parentEl.classList.contains('expanded')) {
                // Use requestAnimationFrame to ensure DOM has updated
                requestAnimationFrame(() => {
                  closeSubmenuBtn.setAttribute('tabindex', '0');
                  closeSubmenuBtn.removeAttribute('disabled');
                  // Use setAttribute to avoid no-param-reassign error
                  closeSubmenuBtn.setAttribute('style', 'pointer-events: auto;');
                });
              } else {
                closeSubmenuBtn.setAttribute('tabindex', '-1');
                // Use setAttribute to avoid no-param-reassign error
                closeSubmenuBtn.setAttribute('style', 'pointer-events: none;');
              }
            }
          } else {
            parentEl.classList.remove('expanded');
            // Reset tabindex to -1 for non-expanded items
            if (closeSubmenuBtn) {
              closeSubmenuBtn.setAttribute('tabindex', '-1');
              // Use setAttribute to avoid no-param-reassign error
              closeSubmenuBtn.setAttribute('style', 'pointer-events: none;');
            }
          }
        });
        
        // Check if any menu is expanded and update body class accordingly
        const hasExpandedMenu = Array.from(expandableMenus).some(el => 
          el.parentElement.classList.contains('expanded')
        );
        
        if (hasExpandedMenu) {
          body.classList.add('has-expanded-mobile-nav-section');
          // it should scroll to the top of the expanded [data-mobile-menu-link] with the expanded class
          const expandedLink = document.querySelector('[data-mobile-menu-link].expanded');
          if (expandedLink) {
            expandedLink.scrollIntoView({ behavior: 'smooth' });
            // Set up focus trap for the expanded section
            trapFocusCleanup = trapFocus(expandedLink);
          }
        } else {
          body.classList.remove('has-expanded-mobile-nav-section');
        }
      });
    });

    // Track the currently expanded navigation item
    let currentExpandedItem = null;
    let escapeListener = null;
    let hoverOpenTimeoutId = null;
    
    // Function to close all dropdowns
    const closeAllDropdowns = () => {
      desktopParentNavItems.forEach((el) => {
        el.classList.remove('expanded');
      });
      mainContentOverlay.classList.add('hidden');
      currentExpandedItem = null;
      
      // Remove dropdown-expanded class from header section and restore bg-transparent if needed
      const headerSection = document.querySelector('[data-section="header"]');
      if (headerSection) {
        headerSection.classList.remove('dropdown-expanded');
        
        // Restore bg-transparent if header has hero-transparent-active and not scrolled
        if (headerSection.classList.contains('hero-transparent-active') && window.scrollY <= SCROLL_THRESHOLD) {
          if (overlayMenu) {
            overlayMenu.classList.remove('bg-white');
            overlayMenu.classList.add('bg-transparent');
          } else if (mobileNavMain) {
            mobileNavMain.classList.remove('bg-white');
            mobileNavMain.classList.add('bg-transparent');
          }
        }
      }
      
      // Remove escape listener
      if (escapeListener) {
        document.removeEventListener('keyup', escapeListener);
        escapeListener = null;
      }
    };
    
    // Function to open dropdown for an item
    const openDropdown = (navItem) => {
      // Close others first
      desktopParentNavItems.forEach((el) => {
        if (el !== navItem) {
          el.classList.remove('expanded');
        }
      });
      
      navItem.classList.add('expanded');
      mainContentOverlay.classList.remove('hidden');
      currentExpandedItem = navItem;
      
      // Add dropdown-expanded class to header section and remove bg-transparent
      const headerSection = document.querySelector('[data-section="header"]');
      if (headerSection) {
        headerSection.classList.add('dropdown-expanded');
        
        // Remove bg-transparent when dropdown is expanded
        if (overlayMenu && overlayMenu.classList.contains('bg-transparent')) {
          overlayMenu.classList.remove('bg-transparent');
          overlayMenu.classList.add('bg-white');
        } else if (mobileNavMain && mobileNavMain.classList.contains('bg-transparent')) {
          mobileNavMain.classList.remove('bg-transparent');
          mobileNavMain.classList.add('bg-white');
        }
      }
      
      // Add escape listener if not already added
      if (!escapeListener) {
        escapeListener = (e) => {
          // Only handle escape for desktop nav when mobile menu is not open
          if (e.key === 'Escape' && !body.classList.contains('open-menu')) {
            closeAllDropdowns();
          }
        };
        document.addEventListener('keyup', escapeListener);
      }
    };
    
    desktopNavItems.forEach((item) => {
      item.addEventListener('focus', () => {
        // Cancel any pending hover-open when focusing via keyboard
        if (hoverOpenTimeoutId) {
          clearTimeout(hoverOpenTimeoutId);
          hoverOpenTimeoutId = null;
        }
        const parentNavItem = item.closest('[data-nav-item]');
        
        if (item.tagName === 'SPAN' && parentNavItem) {
          openDropdown(parentNavItem);
        } else {
          closeAllDropdowns();
        }
      });
    });
    
    // Handle keyboard navigation within dropdowns
    desktopParentNavItems.forEach((navItem) => {
      const navLink = navItem.querySelector('[data-nav-link]');
      const submenu = navItem.querySelector('.desktop-submenu-dropdown');
      
      if (!navLink || !submenu || navLink.tagName !== 'SPAN') return;
      
      // Handle keyboard navigation when dropdown is open
      navItem.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && navItem.classList.contains('expanded')) {
          const focusableElements = submenu.querySelectorAll(
            'a[href]:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          
          if (focusableElements.length === 0) return;
          
          const firstFocusable = focusableElements[0];
          const lastFocusable = focusableElements[focusableElements.length - 1];
          
          // If tab on nav link, focus first item in dropdown
          if (!e.shiftKey && document.activeElement === navLink) {
            e.preventDefault();
            firstFocusable.focus();
          }
          // If shift+tab on first dropdown item, go back to nav link
          else if (e.shiftKey && document.activeElement === firstFocusable) {
            e.preventDefault();
            navLink.focus();
          }
          // If tab on last dropdown item, let natural tab happen and close dropdown
          else if (!e.shiftKey && document.activeElement === lastFocusable) {
            // Don't prevent default - let tab naturally move to next element
            setTimeout(() => {
              // Check if focus left the nav item entirely
              if (!navItem.contains(document.activeElement)) {
                navItem.classList.remove('expanded');
                // Check if focus moved to another nav item
                const focusedNavItem = Array.from(desktopParentNavItems).find(item => 
                  item.contains(document.activeElement)
                );
                if (!focusedNavItem) {
                  // Focus left navigation entirely
                  closeAllDropdowns();
                } else {
                  // Check if any dropdown is still expanded
                  const anyExpanded = Array.from(desktopParentNavItems).some(el => 
                    el.classList.contains('expanded')
                  );
                  if (!anyExpanded) {
                    // Remove dropdown-expanded class and restore bg-transparent if needed
                    const headerSection = document.querySelector('[data-section="header"]');
                    if (headerSection) {
                      headerSection.classList.remove('dropdown-expanded');
                      
                      // Restore bg-transparent if header has hero-transparent-active and not scrolled
                      if (headerSection.classList.contains('hero-transparent-active') && window.scrollY <= SCROLL_THRESHOLD) {
                        if (overlayMenu) {
                          overlayMenu.classList.remove('bg-white');
                          overlayMenu.classList.add('bg-transparent');
                        } else if (mobileNavMain) {
                          mobileNavMain.classList.remove('bg-white');
                          mobileNavMain.classList.add('bg-transparent');
                        }
                      }
                    }
                  }
                }
              }
            }, 0);
          }
          // For all other cases within submenu, let natural tab flow happen
          // No need to do anything - natural tab flow will work
        }
      });
    });
    
    // Handle focus leaving navigation area completely
    document.addEventListener('focusin', (e) => {
      // Check if focus has moved outside all navigation items
      const isInNavigation = Array.from(desktopParentNavItems).some(item => 
        item.contains(e.target)
      );
      
      // Also check if focus is on main navigation links that aren't in dropdowns
      const isMainNavLink = e.target.closest('.menu-links-list');
      
      if (!isInNavigation && !isMainNavLink && currentExpandedItem) {
        closeAllDropdowns();
      }
    });

    // Track if mouse is currently over any nav item
    let isMouseOverNavigation = false;
    let closeDropdownTimeout = null;
    
    // Handle mouse hover for desktop nav items  
    desktopParentNavItems.forEach((item) => {
      const navLink = item.querySelector('[data-nav-link]');
      if (!navLink || navLink.tagName !== 'SPAN') return;

      item.addEventListener('mouseenter', () => {
        if (hoverOpenTimeoutId) clearTimeout(hoverOpenTimeoutId);
        if (closeDropdownTimeout) clearTimeout(closeDropdownTimeout);
        
        // Apply delay only if coming from outside the navigation area
        const delay = isMouseOverNavigation ? 0 : 300;
        isMouseOverNavigation = true;
        
        hoverOpenTimeoutId = setTimeout(() => {
          openDropdown(item);
          hoverOpenTimeoutId = null;
        }, delay);
      });

      item.addEventListener('mouseleave', () => {
        // Cancel pending delayed open if leaving before timeout completes
        if (hoverOpenTimeoutId) {
          clearTimeout(hoverOpenTimeoutId);
          hoverOpenTimeoutId = null;
        }
        
        // Check if mouse is leaving the entire navigation area
        setTimeout(() => {
          const isStillOverNav = Array.from(desktopParentNavItems).some(navItem => 
            navItem.matches(':hover')
          );
          if (!isStillOverNav) {
            isMouseOverNavigation = false;
          }
        }, 10);
        
        // Delay closing dropdown to prevent flicker when moving between nav items
        if (closeDropdownTimeout) clearTimeout(closeDropdownTimeout);
        closeDropdownTimeout = setTimeout(() => {
          // Only close if keyboard navigation isn't active and mouse isn't over any nav item
          const isStillOverAnyNav = Array.from(desktopParentNavItems).some(navItem => 
            navItem.matches(':hover')
          );
          
          if (currentExpandedItem === item && !item.contains(document.activeElement) && !isStillOverAnyNav) {
            item.classList.remove('expanded');
            if (currentExpandedItem === item) {
              currentExpandedItem = null;
            }
            // Check if any other dropdown is open
            const anyExpanded = Array.from(desktopParentNavItems).some(el => 
              el.classList.contains('expanded')
            );
            if (!anyExpanded) {
              mainContentOverlay.classList.add('hidden');
              // Remove dropdown-expanded class and restore bg-transparent if needed
              const headerSection = document.querySelector('[data-section="header"]');
              if (headerSection) {
                headerSection.classList.remove('dropdown-expanded');
                
                // Restore bg-transparent if header has hero-transparent-active and not scrolled
                if (headerSection.classList.contains('hero-transparent-active') && window.scrollY <= SCROLL_THRESHOLD) {
                  if (overlayMenu) {
                    overlayMenu.classList.remove('bg-white');
                    overlayMenu.classList.add('bg-transparent');
                  } else if (mobileNavMain) {
                    mobileNavMain.classList.remove('bg-white');
                    mobileNavMain.classList.add('bg-transparent');
                  }
                }
              }
            }
          }
        }, 50); // Small delay to prevent flicker when moving between nav items
      });
    });

    // Check if there's a hero section requesting transparent header
    const transparentHeroSection = document.querySelector('[data-transparent-header="true"]');
    const hasTransparentHero = transparentHeroSection && transparentHeroSection.getBoundingClientRect().top <= 200;
    
    // Apply transparent header styling when hero requests it
    if (hasTransparentHero && !overlayMenu && mobileNavMain) {
      // Add overlay-menu class to section for CSS styling
      element.classList.add('overlay-menu', 'hero-transparent-active');
      // Apply transparent background to the inner nav element
      mobileNavMain.classList.remove('bg-white');
      mobileNavMain.classList.add('bg-transparent');
      
      // Track hover state
      let isHovered = false;
      
      // Scroll handler
      const handleHeroTransparentScroll = throttle(() => {
        const isScrolled = window.scrollY > SCROLL_THRESHOLD;
        
        if (isScrolled) {
          // Remove transparency when scrolled
          element.classList.remove('hero-transparent-active');
          mobileNavMain.classList.remove('bg-transparent');
          mobileNavMain.classList.add('bg-white');
        } else if (!isHovered) {
          // Restore transparency when scrolled back up (unless hovered)
          element.classList.add('hero-transparent-active');
          mobileNavMain.classList.remove('bg-white');
          mobileNavMain.classList.add('bg-transparent');
        }
      }, 50);
      
      // Handle hover states
      mobileNavMain.addEventListener('mouseenter', () => {
        isHovered = true;
        mobileNavMain.classList.remove('bg-transparent');
        mobileNavMain.classList.add('bg-white');
      });
      
      mobileNavMain.addEventListener('mouseleave', () => {
        isHovered = false;
        // Only return to transparent if not scrolled past threshold
        if (window.scrollY <= SCROLL_THRESHOLD) {
          mobileNavMain.classList.remove('bg-white');
          mobileNavMain.classList.add('bg-transparent');
        }
      });
      
      // Add scroll listener
      window.addEventListener('scroll', handleHeroTransparentScroll);
      
    } else if (hasTransparentHero && overlayMenu) {
      // If already an overlay menu, just add the hero-triggered class
      element.classList.add('hero-transparent-active');
    }
    
    // Set initial state and add scroll listener only if overlay menu exists
    if (overlayMenu) {
      handleHeaderScroll();
      window.addEventListener('scroll', handleHeaderScroll);
    }

    // Add secondary nav scroll handling
    if (secondaryNav) {
      window.addEventListener('scroll', handleSecondaryNavScroll);
    }

    // Add general scroll handler for mobile menu position updates
    window.addEventListener('scroll', handleGeneralScroll);

    updateHeaderPosition();
    updateMobileMenuPosition();
    closeMenuTrigger.addEventListener('click', closeMobileMenu);
    
    // Handle all close submenu triggers - they should close their parent expanded section
    closeMobileSubMenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const parentSection = trigger.closest('[data-mobile-menu-link]');
        if (parentSection && parentSection.classList.contains('expanded')) {
          const expandableButton = parentSection.querySelector('[data-expandable-nav]');
          if (expandableButton) {
            expandableButton.click();
          }
        }
      });
    });
    
    mobileMenuTrigger.addEventListener('click', toggleMobileMenu);
    if (mobileMenuOverlay) {
      mobileMenuOverlay.addEventListener('click', closeMobileMenu);
    }
    window.addEventListener('resize', () => {
      updateHeaderPosition();
      updateMobileMenuPosition();
    });

    // Update cleanup to only remove scroll listener if it was added
    return () => {
      if (overlayMenu) {
        window.removeEventListener('scroll', handleHeaderScroll);
        if (headerStateTimeout) clearTimeout(headerStateTimeout);
        if (rafId) cancelAnimationFrame(rafId);
      }
      if (hoverOpenTimeoutId) {
        clearTimeout(hoverOpenTimeoutId);
        hoverOpenTimeoutId = null;
      }
      if (closeDropdownTimeout) {
        clearTimeout(closeDropdownTimeout);
        closeDropdownTimeout = null;
      }
      if (secondaryNav) {
        window.removeEventListener('scroll', handleSecondaryNavScroll);
      }
      window.removeEventListener('scroll', handleGeneralScroll);
      window.removeEventListener('resize', () => {
        updateHeaderPosition();
        updateMobileMenuPosition();
      });
    };
  };

  // SEARCH EVENTS:
  const searchHandler = () => {
    if (!searchTrigger || !searchDrawer) return;

    const openSearch = () => {
      document.body.classList.add('no-scroll');
      searchDrawer.classList.toggle('invisible');
      searchDrawer.classList.toggle('pointer-events-none');
    };

    const closeSearch = () => {
      document.body.classList.remove('no-scroll');
      searchDrawer.classList.toggle('invisible');
      searchDrawer.classList.toggle('pointer-events-none');
    };

    searchTrigger.forEach((searchEl) => {
      searchEl.addEventListener('click', openSearch);

      // if search trigger has .custom-search-drawer__input class then it should openSearch on focus
      if (searchEl.classList.contains('custom-search-drawer__input')) {
        searchEl.addEventListener('focus', openSearch);
      }
    });

    searchDrawerClose.addEventListener('click', closeSearch);

    searchDrawer.addEventListener('click', (e) => {
      if (e.target.dataset.searchDrawer) closeSearch();
    });
  };

  const initSearchResultSlider = () =>
    !!searchDrawerSlider &&
    new SwiperCarousel(searchDrawerSlider, {
      slidesPerView: 2.1,
      spaceBetween: 10.5,
      allowTouchMove: true,
      breakpoints: {
        768: {
          slidesPerView: 3.2,
        },
        1024: {
          spaceBetween: 15,
          slidesPerView: 3.8,
        },
        1440: {
          spaceBetween: 15,
          slidesPerView: 4.8,
        },
      },
      navigation: {
        nextEl: searchDrawerSliderNext,
        prevEl: searchDrawerSliderPrev,
      },
      scrollbar: {
        el: searchDrawerScrollbal,
        draggable: true,
      },
      mousewheel: {
        forceToAxis: true,
      },
    });

  // Handle duplicated learn button functionality
  const initDuplicatedLearnButton = () => {
    const duplicatedLearnBtn = document.getElementById('duplicated-learn-btn');
    if (duplicatedLearnBtn) {
      duplicatedLearnBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const originalBtn = document.querySelector('.menu-links-container > .mobile-nav-section:last-child button');
        if (originalBtn) {
          originalBtn.click();
        }
      });
    }
  };

  // Initialize rolodex animation
  const initRolodexAnimation = () => {
    const rolodexContainer = element.querySelector('[data-search-rolodex]');
    if (rolodexContainer) {
      const rotationInterval = data.searchPlaceholderRotationInterval || 3000;
      // Initialize the RolodexAnimation (stores internally for cleanup)
      // eslint-disable-next-line no-new
      new RolodexAnimation(rolodexContainer, rotationInterval);
    }
  };

  const init = () => {
    headerEventsHandler();
    searchHandler();
    initSearchResultSlider();
    initDuplicatedLearnButton();
    initRolodexAnimation();
  };

  // Remove opacity-0 if no transparent hero section is present
  const removeHeaderOpacity = () => {
    const transparentHeroSection = document.querySelector('[data-transparent-header="true"]');
    const headerElement = element;
    
    // If there's no transparent hero section on the page, remove opacity-0
    if (!transparentHeroSection && headerElement) {
      headerElement.classList.remove('opacity-0');
      headerElement.classList.add('opacity-100');
    }
  };

  init();
  
  // Check and remove opacity on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeHeaderOpacity);
  } else {
    removeHeaderOpacity();
  }
};
